import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectOutlook } from '../store/slices/outlookSlice';
import { print } from '../config/settings';

// Create Print Context
const PrintContext = createContext({
  openPrintDialog: () => {},
  isPrintDialogOpen: false,
  printData: null,
});

/**
 * Print Provider component with complete print functionality
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} PrintProvider component
 */
export const PrintProvider = ({ children }) => {
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const outlook = useSelector(selectOutlook).value;

  /**
   * Clean entity name for PDF filename
   * @param {string} name - Entity name to clean
   * @returns {string} Cleaned name
   */
  const cleanName = useCallback((name) => name
    .replaceAll(' ', '_') // Replace all spaces with "_"
    .normalize('NFD') // Normalize to unicode so that accents are unique characters
    .replaceAll(/([\u0300-\u036F]|[^0-9a-zA-Z_-])/g, ''), []); // Remove unique characters accents and all characters not wanted

  /**
   * Generate PDF filename and set document title
   * @param {Object} data - Print data with code, name, outlook
   */
  const setPdfTitle = useCallback((data) => {
    const { code = '', name = '' } = data;
    const cleanedName = cleanName(name);

    const titleItems = ['CLIMADIAG', code, cleanedName];
    if (outlook) {
      titleItems.push(outlook);
    }

    document.title = titleItems.join('_');
  }, [cleanName, outlook]);

  /**
   * Open print dialog with entity data
   * @param {Object} data - Print data {code, name, outlook}
   */
  const openPrintDialog = useCallback((data) => {
    if (!globalThis.print) {
      // eslint-disable-next-line no-alert
      globalThis.alert(print.error);
      return;
    }

    setPrintData(data);
    setPdfTitle(data);

    // Check if user wants to skip tutorial
    if (localStorage.getItem('CLIMADIAG_hide_tutorial') === 'true') {
      globalThis.print();
    } else {
      setIsPrintDialogOpen(true);
    }
  }, [setPdfTitle]);

  /**
   * Close print dialog
   */
  const closePrintDialog = useCallback(() => {
    setIsPrintDialogOpen(false);
    setPrintData(null);
  }, []);

  const value = {
    openPrintDialog,
    closePrintDialog,
    isPrintDialogOpen,
    printData,
  };

  return (
    <PrintContext.Provider value={value}>
      {children}
    </PrintContext.Provider>
  );
};

/**
 * Hook to use Print context
 * @returns {Object} Print context value
 */
export const usePrint = () => {
  const context = useContext(PrintContext);
  if (!context) {
    throw new Error('usePrint must be used within a PrintProvider');
  }
  return context;
};

export default PrintContext;
